// src/pages/EntryDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import "./EntryDetail.css";

const EntryDetail = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [entry, setEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState({
    type: "IN",
    amount: "",
    paymentBy: "",
    paymentMode: "",
    date: "",
    note: "",
    invoice: null,
    existingInvoiceUrl: null,
  });

  // -------------------------------------------------------
  // FETCH ENTRY
  // -------------------------------------------------------
  const fetchEntry = async () => {
    try {
      const res = await api.get(`/entries/${entryId}`);
      const fetched = res.data.entry;

      setEntry(fetched);

      setForm({
        type: fetched.type,
        amount: fetched.amount,
        paymentBy: fetched.paymentBy || "",
        paymentMode: fetched.paymentMode || "",
        date: fetched.date,
        note: fetched.note || "",
        invoice: null,
        existingInvoiceUrl: fetched.invoiceUrl || null,
      });
    } catch (err) {
      console.error(err);
      alert("Error loading entry");
    }
  };

  useEffect(() => {
    fetchEntry();
  }, [entryId]);

  // -------------------------------------------------------
  // PERMISSION CHECK (Only creator can edit/delete)
  // -------------------------------------------------------
  const entryMobile = entry?.createdByMobile?.toString();
  const userMobile = user?.mobile?.toString();
  const isOwnerOfEntry = entryMobile === userMobile;

  // -------------------------------------------------------
  // UPDATE ENTRY
  // -------------------------------------------------------
  const updateEntry = async () => {
    try {
      const formData = new FormData();

      formData.append("type", form.type);
      formData.append("amount", form.amount);
      formData.append("paymentBy", form.paymentBy);
      formData.append("paymentMode", form.paymentMode);
      formData.append("note", form.note);
      formData.append("date", form.date);

      if (form.invoice) {
        formData.append("invoice", form.invoice);
      }

      await api.patch(`/entries/${entryId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Entry updated successfully");
      setIsEditing(false);
      fetchEntry();
    } catch (err) {
      console.error(err);
      alert("Failed to update entry");
    }
  };

  // -------------------------------------------------------
  // DELETE ENTRY
  // -------------------------------------------------------
  const handleDelete = async () => {
    if (!window.confirm("Delete this entry?")) return;

    try {
      await api.delete(`/entries/${entryId}`);
      navigate(`/books/${entry.book}`);
    } catch (err) {
      console.error(err);
      alert("Error deleting entry");
    }
  };

  // -------------------------------------------------------
  // UI
  // -------------------------------------------------------
  if (!entry) {
    return (
      <div className="ed-loading">
        <div className="spinner"></div>
        Loading...
      </div>
    );
  }

  const isCashIn = entry.type === "IN";
  const sign = isCashIn ? "+" : "-";

  return (
    <div className="entry-detail-page">
      {/* HEADER */}
      <header
        className={`ed-header ${isCashIn ? "header-green" : "header-red"}`}
      >
        <button className="ed-back-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i>
        </button>

        <div className="ed-header-text">
          <span className="ed-header-sub">Entry</span>
          <h1 className="ed-header-title">
            {isCashIn ? "Cash In" : "Cash Out"}
          </h1>
        </div>

        <span className={`ed-type-pill ${isCashIn ? "pill-in" : "pill-out"}`}>
          {isCashIn ? "IN" : "OUT"}
        </span>
      </header>

      {/* CONTENT */}
      <div className="ed-content">
        {/* AMOUNT CARD */}
        <div className="ed-card ed-amount-card">
          <div className="ed-amount-icon">
            {isCashIn ? "⬇️" : "⬆️"}
          </div>
          <div>
            <div className="ed-amount-label">Total Amount</div>
            {isEditing ? (
              <input
                className="ed-input big-input"
                type="number"
                value={form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value })
                }
              />
            ) : (
              <div
                className={`ed-amount-value ${
                  isCashIn ? "amount-in" : "amount-out"
                }`}
              >
                {sign}₹{entry.amount}
              </div>
            )}
          </div>
        </div>

        {/* INFO CARD */}
        <div className="ed-card ed-info-card">
          {/* DATE */}
          <div className="ed-info-row">
            <div className="ed-info-left">
              <span className="ed-info-icon">📅</span>
              <span className="ed-info-label">Date & Time</span>
            </div>
            {isEditing ? (
              <input
                type="datetime-local"
                className="ed-input"
                value={form.date?.slice(0, 16)}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            ) : (
              <span className="ed-info-value">
                {new Date(entry.date).toLocaleString()}
              </span>
            )}
          </div>

          {/* PAYMENT BY */}
          <div className="ed-info-row">
            <div className="ed-info-left">
              <span className="ed-info-icon">👤</span>
              <span className="ed-info-label">Payment By</span>
            </div>
            {isEditing ? (
              <input
                className="ed-input"
                value={form.paymentBy}
                onChange={(e) =>
                  setForm({ ...form, paymentBy: e.target.value })
                }
                placeholder="Client / Vendor / Person"
              />
            ) : (
              <span className="ed-info-value">{entry.paymentBy || "-"}</span>
            )}
          </div>

          {/* PAYMENT MODE */}
          <div className="ed-info-row">
            <div className="ed-info-left">
              <span className="ed-info-icon">💳</span>
              <span className="ed-info-label">Payment Mode</span>
            </div>
            {isEditing ? (
              <select
                className="ed-input"
                value={form.paymentMode}
                onChange={(e) =>
                  setForm({ ...form, paymentMode: e.target.value })
                }
              >
                <option value="">Select Mode</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Card">Card</option>
                <option value="Other">Other</option>
              </select>
            ) : (
              <span className="ed-info-value">{entry.paymentMode || "-"}</span>
            )}
          </div>

          {/* CREATED BY */}
          <div className="ed-info-row">
            <div className="ed-info-left">
              <span className="ed-info-icon">🧾</span>
              <span className="ed-info-label">Added By</span>
            </div>
            <span className="ed-info-value">{entry.createdByName}</span>
          </div>
        </div>

        {/* NOTE CARD */}
        <div className="ed-card ed-note-card">
          <div className="ed-note-header">
            <span className="ed-note-icon">📝</span>
            <span className="ed-note-title">Note</span>
          </div>

          {isEditing ? (
            <textarea
              className="ed-input note-input"
              rows={3}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Add a note for this entry"
            />
          ) : (
            <p className="ed-note-text">
              {entry.note || "No note added for this entry."}
            </p>
          )}
        </div>

        {/* INVOICE CARD */}
        <div className="ed-card ed-invoice-card">
          <div className="ed-note-header">
            <span className="ed-note-icon">📎</span>
            <span className="ed-note-title">Invoice / Attachment</span>
          </div>

          {isEditing ? (
            <div className="ed-invoice-edit">
              <input
                type="file"
                className="ed-input-file"
                accept="image/*,application/pdf"
                onChange={(e) =>
                  setForm({ ...form, invoice: e.target.files[0] })
                }
              />
              {form.existingInvoiceUrl && (
                <a
                  href={form.existingInvoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ed-link"
                >
                  View existing invoice
                </a>
              )}
            </div>
          ) : entry.invoiceUrl ? (
            <a
              href={entry.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              className="ed-link"
            >
              View Invoice
            </a>
          ) : (
            <p className="ed-note-text">No invoice uploaded.</p>
          )}
        </div>
      </div>

      {/* STICKY BOTTOM ACTION BAR */}
      {isOwnerOfEntry && (
        <div className="ed-bottom-bar">
          {isEditing ? (
            <>
              <button className="ed-btn secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </button>
              <button
                className="ed-btn primary"
                onClick={updateEntry}
              >
                Save Changes
              </button>
            </>
          ) : (
            <>
              <button
                className="ed-btn ghost"
                onClick={handleDelete}
              >
                Delete
              </button>
              <button
                className="ed-btn primary"
                onClick={() => setIsEditing(true)}
              >
                Edit Entry
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EntryDetail;
